/* global Office */
import React, { useState } from "react";
import { Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import { saveToken, getStoredToken } from "../../auth/tokenStore";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    gap: "16px",
  },
  title: { color: tokens.colorNeutralForeground1 },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "12px",
    textAlign: "center",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
});

export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredToken());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const styles = useStyles();

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const handleSignIn = () => {
    setError(null);
    setLoading(true);

    Office.context.ui.displayDialogAsync(
      `${window.location.origin}/auth-dialog.html`,
      { height: 60, width: 30, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          setError(`Could not open sign-in dialog: ${asyncResult.error.message}`);
          setLoading(false);
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (messageEvent: any) => {
            dialog.close();
            try {
              const data = JSON.parse(messageEvent.message);
              if (data.status === "success" && data.accessToken) {
                saveToken(data.accessToken, data.username, data.expiresIn ?? 3600);
                setIsAuthenticated(true);
              } else {
                setError(data.message ?? "Sign-in failed. Please try again.");
              }
            } catch (e: any) {
              setError(`Unexpected error: ${e?.message ?? String(e)}`);
            }
            setLoading(false);
          }
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (eventArgs: any) => {
            // 12006 fires for both user-dismissed AND programmatic dialog.close()
            // Only treat it as a cancellation if we haven't already set loading=false
            if (eventArgs.error === 12006) {
              setLoading(false);
            }
          }
        );
      }
    );
  };

  return (
    <div className={styles.container}>
      <Text className={styles.title} size={500} weight="semibold">
        Dynamics CRM Connector
      </Text>
      <Text size={300}>Sign in with your Microsoft account to access CRM data.</Text>
      <Button appearance="primary" disabled={loading} onClick={handleSignIn}>
        {loading ? "Signing in..." : "Sign In"}
      </Button>
      {error && <Text className={styles.error}>{error}</Text>}
    </div>
  );
};
